import React from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import type { StudioThemeId } from 'fym-shared/src/music24/studioThemes';
import { TypedMemo } from '../../../../common/types';
import { studioThemeMessages } from '../../studio/studioThemeMessages';
import { overviewMessages } from './overviewMessages';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './Overview.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface CanMakeRowProps {
  themeId: StudioThemeId;
  /** The video credits of one run; 0 for an image. */
  cost: number;
  /** The credits of each video when one run makes several (the countdown), else null: "1 credit each". */
  costEach?: number | null;
  /** A full video on Free (design e5): "Artist plan"; a click opens the plans. */
  planLocked: boolean;
  /** The balance is below the cost (design e6, e7): "Needs N credits"; a click offers credits. */
  creditLocked: boolean;
  onMake: () => void;
  onUpgrade: () => void;
  onOfferCredits: () => void;
}

/** One asset type of "Can still make" (design b1 `.cmr`): the name, its line and its cost. */
const CanMakeRowComponent: React.FC<CanMakeRowProps> = (props) => {
  const { themeId, cost, costEach = null, planLocked, creditLocked, onMake, onUpgrade, onOfferCredits } = props;
  const intl = useIntl();
  const copy = studioThemeMessages[themeId];
  let onClick = onMake;
  if (planLocked) {
    onClick = onUpgrade;
  } else if (creditLocked) {
    onClick = onOfferCredits;
  }
  let costText = intl.formatMessage(overviewMessages.credits, { count: cost });
  if (cost === 0) {
    costText = intl.formatMessage(overviewMessages.free);
  } else if (costEach !== null) {
    costText = intl.formatMessage(overviewMessages.creditsEach, { count: costEach });
  }
  if (planLocked) {
    costText = intl.formatMessage(overviewMessages.proPlan);
  }

  return (
    <button
      type="button"
      className={clsx(styles.makeRow, (planLocked || creditLocked) && styles.makeLocked)}
      onClick={onClick}
      data-testid={`can-make-${themeId}`}
    >
      <span className={styles.makeText}>
        <b className={styles.makeName}>{intl.formatMessage(copy.name)}</b>
        <small className={styles.makeLine}>
          {creditLocked
            ? intl.formatMessage(overviewMessages.needsCredits, { count: cost })
            : intl.formatMessage(copy.description)}
        </small>
      </span>
      <span className={clsx(styles.cost, cost === 0 && !planLocked && styles.costFree)}>{costText}</span>
    </button>
  );
};

export const CanMakeRow = TypedMemo(CanMakeRowComponent);
